import React, {
  useState, useRef, useCallback, useMemo, useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
  ChevronDown, ChevronUp, Copy, Plus, Download, Upload, X, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiPatch } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import type {
  ContractTemplate, VariablesData, TemplateListItem, SaveState,
  CustomVariable, ContractSection, VarAutoItem,
} from './types';
import { getDiagnostics } from './helpers';
import { downloadJSON, createTemplateExport, type ExportedTemplate } from './importExport';
import SaveIndicator from './SaveIndicator';
import TemplateSwitcher from './TemplateSwitcher';
import SectionCard, { type SectionCardHandle } from './SectionCard';
import VariablesSidebar from './VariablesSidebar';
import VariablesReference from './VariablesReference';
import CustomVarsManager from './CustomVarsManager';
import PreviewPanel from './PreviewPanel';

// ── Template Editor ───────────────────────────────────────────────────────────

interface TemplateEditorProps {
  template: ContractTemplate;
  variablesData: VariablesData | undefined;
  templateList: TemplateListItem[];
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string) => void;
  isAdmin: boolean;
  isLoading: boolean;
  isLoadingTemplates: boolean;

  // Template CRUD
  onNewTemplate: (name: string) => Promise<any>;
  onDeleteTemplate: (id: string) => Promise<any>;
  onSetDefault: (id: string) => Promise<any>;
  onDuplicateTemplate: () => void;
  isDuplicating: boolean;
  onSeedDefault: () => void;
  isSeeding: boolean;
  onAddSection: () => void;
  isAddingSection: boolean;

  // Title
  contractTitle: string | null;
  onTitleChange: (value: string) => void;

  // Fullscreen
  isFullscreen: boolean;
  toggleFullscreen: () => void;

  // Import
  onTemplateImported: (newId: string | null) => void;
}

export default function TemplateEditor({
  template, variablesData, templateList, selectedTemplateId,
  onSelectTemplate, isAdmin, isLoading, isLoadingTemplates,
  onNewTemplate, onDeleteTemplate, onSetDefault,
  onDuplicateTemplate, isDuplicating,
  onSeedDefault, isSeeding, onAddSection, isAddingSection,
  contractTitle, onTitleChange,
  isFullscreen, toggleFullscreen, onTemplateImported,
}: TemplateEditorProps) {
  const qc = useQueryClient();

  // ── Internal editor state ──────────────────────────────────────────────────
  const dragItem = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [liveBodyOverrides, setLiveBodyOverrides] = useState<Record<string, string>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<ExportedTemplate | null>(null);
  const [importName, setImportName] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);

  // Map sectionId → ref handle (for external variable insertion)
  const sectionRefs = useRef<Record<string, SectionCardHandle | null>>({});

  // ── Derived data ──────────────────────────────────────────────────────────

  const sections = template.sections ?? [];

  let customVars: CustomVariable[] = template.customVariables ?? [];
  if (typeof customVars === 'string') {
    try { customVars = JSON.parse(customVars as unknown as string); } catch { customVars = []; }
  }
  if (!Array.isArray(customVars)) customVars = [];

  const allVariables: VarAutoItem[] = useMemo(() => [
    ...(variablesData?.builtin ?? []).map(v => ({ key: v.key, desc: v.desc })),
    ...customVars.map(v => ({ key: v.key, desc: v.label, isCustom: true as const })),
  ], [variablesData, customVars]);

  const diagnostics = getDiagnostics(sections, customVars, liveBodyOverrides);

  const globalSaveState: SaveState = Object.values(saveStates).includes('saving')
    ? 'saving'
    : Object.values(saveStates).includes('error')
      ? 'error'
      : 'idle';

  // ── Drag & drop handlers ──────────────────────────────────────────────────

  const reorderMutation = useMutation({
    mutationFn: (sections: { id: string; order: number }[]) =>
      apiPatch('/contract-template/sections/reorder', { sections }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contract-template'] }),
  });

  const handleDragStart = useCallback((_e: React.DragEvent, id: string) => { dragItem.current = id; }, []);
  const handleDragOver = useCallback((e: React.DragEvent, id: string) => { e.preventDefault(); setDragOverId(id); }, []);
  const handleDrop = useCallback((_e: React.DragEvent, targetId: string) => {
    setDragOverId(null);
    if (!dragItem.current || dragItem.current === targetId) return;
    const secs = [...sections];
    const fromIdx = secs.findIndex(s => s.id === dragItem.current);
    const toIdx = secs.findIndex(s => s.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = secs.splice(fromIdx, 1);
    secs.splice(toIdx, 0, moved);
    reorderMutation.mutate(secs.map((s, i) => ({ id: s.id, order: i })));
  }, [sections, reorderMutation]);

  // ── Editing handlers ──────────────────────────────────────────────────────

  const handleBodyChange = useCallback((sectionId: string, body: string) => {
    setLiveBodyOverrides(prev => ({ ...prev, [sectionId]: body }));
  }, []);

  const handleSaveStateChange = useCallback((sectionId: string, state: SaveState) => {
    setSaveStates(prev => ({ ...prev, [sectionId]: state }));
  }, []);

  const handleInsertVariable = useCallback((token: string) => {
    if (activeSectionId) {
      const handle = sectionRefs.current[activeSectionId];
      if (handle) {
        handle.insertAtCursor(token);
        return;
      }
    }
    navigator.clipboard.writeText(token).then(() => toast.success(`${token} copiato`));
  }, [activeSectionId]);

  const handleSetActive = useCallback((id: string) => { setActiveSectionId(id); }, []);

  // ── Export handlers ───────────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    if (!template) return;
    const exported = createTemplateExport(template);
    downloadJSON(exported, `template-${template.name.toLowerCase().replace(/\s+/g, '-')}.json`);
    toast.success('Template scaricato');
  }, [template]);

  const handleImportFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string) as ExportedTemplate;
        if (data.version !== '1' || !data.template) {
          toast.error('Formato file non valido');
          return;
        }
        setImportFile(data);
        setImportName(data.template.name);
      } catch {
        toast.error('Errore lettura file');
      }
    };
    reader.readAsText(file);
  }, []);

  const importMutation = useMutation({
    mutationFn: (data: { name: string; title: string | null; customVariables: CustomVariable[]; sections: any[] }) =>
      apiPost('/contract-template/import', data),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['contract-template-list'] });
      onTemplateImported(res.data?.id || null);
      setShowImportModal(false);
      setImportFile(null);
      setImportName('');
      toast.success('Template importato');
    },
    onError: (e: Error) => toast.error(e.message || 'Errore import'),
  });

  const handleImportConfirm = useCallback(async () => {
    if (!importFile || !importName.trim()) {
      toast.error('Nome obbligatorio');
      return;
    }
    const sections = importFile.template.sections.map(({ id, createdAt, updatedAt, templateId, ...rest }: any) => rest);
    importMutation.mutate({
      name: importName,
      title: importFile.template.title,
      customVariables: importFile.template.customVariables || [],
      sections,
    });
  }, [importFile, importName, importMutation]);

  // ── Section expand/collapse ───────────────────────────────────────────────

  const expandAll = useCallback(() => setExpandedSections(new Set(sections.map(s => s.id))), [sections]);
  const collapseAll = useCallback(() => setExpandedSections(new Set()), []);
  const toggleExpanded = useCallback((id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '2rem', textAlign: 'center' }}>
        Caricamento template…
      </div>
    );
  }

  const outerStyle = isFullscreen
    ? { position: 'fixed' as const, top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' as const }
    : { background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' as const };
  const flexStyle = isFullscreen
    ? { display: 'flex' as const, flex: 1, minHeight: 0, gap: 0, overflow: 'hidden' as const }
    : { display: 'flex' as const, height: 'calc(100vh - 220px)', minHeight: 600, gap: 0 };

  const jsx = (
    <div style={outerStyle}>
      {isFullscreen && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.5rem 1rem',
          borderBottom: '1px solid rgba(255,255,255,0.10)',
          background: 'var(--glass-bg-card)',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Contract Template — Schermo intero</span>
          <button
            onClick={toggleFullscreen}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.10)',
              cursor: 'pointer', padding: '0.3rem 0.4rem',
              borderRadius: 'var(--radius-interactive)',
              color: 'var(--color-text-muted)',
              display: 'flex', alignItems: 'center',
            }}
            title="Esci da schermo intero"
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div style={flexStyle}>
        {/* ── Left panel ─────────────────────────────────────────────── */}
        <div style={{
          width: 460, minWidth: 340, maxWidth: 500,
          borderRight: '1px solid rgba(255,255,255,0.10)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--color-bg)',
        }}>
          {/* Panel header */}
          <div style={{
            padding: '0.75rem 0.875rem',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            background: 'var(--glass-bg-card)',
            flexShrink: 0,
          }}>
            {/* Template switcher */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: 10, color: 'var(--color-text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '0.35rem' }}>
                Template
              </label>
              <TemplateSwitcher
                templates={templateList}
                selectedId={selectedTemplateId}
                onSelect={onSelectTemplate}
                onNew={onNewTemplate}
                onDelete={onDeleteTemplate}
                onSetDefault={onSetDefault}
                isAdmin={isAdmin}
                isLoading={isLoadingTemplates}
              />
              {isAdmin && selectedTemplateId && (
                <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download size={12} />}
                    onClick={handleExport}
                    title="Scarica template"
                  >
                    Esporta
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Upload size={12} />}
                    onClick={() => setShowImportModal(true)}
                    title="Importa template"
                  >
                    Importa
                  </Button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', flex: 1 }}>Editor Template</span>
              <SaveIndicator state={globalSaveState} />
              {sections.length > 0 && isAdmin && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<ChevronDown size={12} />}
                    onClick={expandAll}
                    title="Espandi tutte"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<ChevronUp size={12} />}
                    onClick={collapseAll}
                    title="Ripiega tutte"
                  />
                </>
              )}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Copy size={12} />}
                  onClick={onDuplicateTemplate}
                  loading={isDuplicating}
                >
                  Duplica
                </Button>
              )}
            </div>
            {/* Contract title input */}
            <div style={{
              marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem'
            }}>
              <label style={{ fontSize: 10, color: 'var(--color-text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Titolo contratto
              </label>
              <input
                value={contractTitle || ''}
                onChange={e => onTitleChange(e.target.value)}
                placeholder="CONTRATTO DI SERVIZI FOTOGRAFICI (default)"
                style={{
                  width: '100%', padding: '0.35rem 0.5rem',
                  border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
                  background: 'var(--glass-bg-card)', color: 'var(--color-text)',
                  fontSize: 'var(--text-sm)', fontWeight: 600,
                }}
              />
              {!contractTitle && (
                <span style={{ fontSize: 10, color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                  Verrà usato il titolo predefinito
                </span>
              )}
            </div>
          </div>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {/* Empty state */}
            {sections.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '2rem 1rem', color: 'var(--color-text-faint)', textAlign: 'center', gap: '1rem',
              }}>
                <FileText size={32} style={{ opacity: 0.3 }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Nessuna sezione</p>
                  <p style={{ fontSize: 11, marginTop: '0.25rem' }}>Aggiungi la prima sezione per iniziare a costruire il contratto.</p>
                </div>
                {isAdmin && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onSeedDefault}
                    loading={isSeeding}
                  >
                    Carica template base
                  </Button>
                )}
              </div>
            )}

            {/* Sections */}
            {sections.map(section => (
              <SectionCard
                key={section.id}
                ref={(el) => { sectionRefs.current[section.id] = el; }}
                section={section}
                isAdmin={isAdmin}
                isActive={activeSectionId === section.id}
                isExpanded={expandedSections.has(section.id)}
                onSetActive={handleSetActive}
                onToggleExpanded={toggleExpanded}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                dragOverId={dragOverId}
                onBodyChange={handleBodyChange}
                onSaveStateChange={handleSaveStateChange}
                saveState={saveStates[section.id] ?? 'idle'}
                allVariables={allVariables}
              />
            ))}

            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus size={13} />}
                onClick={onAddSection}
                loading={isAddingSection}
                style={{ alignSelf: 'flex-start' }}
              >
                Aggiungi sezione
              </Button>
            )}

            {/* Variables sidebar */}
            <VariablesSidebar
              variables={variablesData}
              customVars={customVars}
              activeSectionId={activeSectionId}
              onInsert={handleInsertVariable}
            />

            {/* Variables reference */}
            <VariablesReference variables={variablesData} customVars={customVars} />

            {/* Custom variables manager */}
            {isAdmin && (
              <CustomVarsManager customVars={customVars} templateId={template.id} />
            )}
          </div>
        </div>

        {/* ── Right panel: A4 preview ─────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PreviewPanel
            sections={sections}
            customVars={customVars}
            liveBodyOverrides={liveBodyOverrides}
            diagnostics={diagnostics}
            contractTitle={contractTitle}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
        </div>

        {/* Import modal */}
        {showImportModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, backdropFilter: 'blur(2px)',
          }} onClick={() => setShowImportModal(false)}>
            <div style={{
              background: 'var(--glass-bg-card)', padding: '2rem', borderRadius: 'var(--radius-card)',
              maxWidth: 'min(600px, 90vw)', maxHeight: '80vh', overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }} onClick={e => e.stopPropagation()}>
              <h2 style={{ marginBottom: '1.5rem', fontSize: 'var(--text-lg)', fontWeight: 700 }}>
                Importa Template
              </h2>

              {!importFile ? (
                <div>
                  <div
                    onDrop={e => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file?.type === 'application/json') handleImportFile(file);
                      else toast.error('Solo file JSON');
                    }}
                    onDragOver={e => e.preventDefault()}
                    style={{
                      border: '2px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
                      padding: '2rem', textAlign: 'center', marginBottom: '1.5rem',
                      background: 'var(--glass-bg-card)', cursor: 'pointer',
                      transition: 'all 200ms ease',
                    }}
                    onClick={() => importInputRef.current?.click()}
                  >
                    <Upload size={32} style={{ opacity: 0.5, margin: '0 auto 0.5rem' }} />
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Trascina file JSON qui</p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>o clicca per sfogliare</p>
                  </div>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".json"
                    onChange={e => e.target.files?.[0] && handleImportFile(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    background: 'var(--glass-bg-card)', padding: '1rem', borderRadius: 'var(--radius-card)',
                    marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.10)',
                  }}>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', marginBottom: '0.5rem' }}>
                      Anteprima
                    </p>
                    <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                      {importFile.template.name}
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>
                      {importFile.template.sections.length} sezioni · {importFile.template.customVariables?.length || 0} variabili custom
                    </p>
                  </div>

                  <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Nome template importato
                  </label>
                  <input
                    autoFocus
                    value={importName}
                    onChange={e => setImportName(e.target.value)}
                    placeholder="Es: Standard - Copia"
                    style={{
                      width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 'var(--radius-interactive)', marginBottom: '1rem',
                      background: 'var(--glass-bg-card)', color: 'var(--color-text)',
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                {importFile && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setImportFile(null); setImportName(''); }}
                  >
                    Carica altro file
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => setShowImportModal(false)}>
                  Annulla
                </Button>
                {importFile && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleImportConfirm}
                    loading={importMutation.isPending}
                  >
                    Importa
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return isFullscreen ? ReactDOM.createPortal(jsx, document.body) : jsx;
}
